import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container, Row, Col, Card, Button, Form, FormGroup, Label, Input, Alert, Spinner,
  Progress, Table, Badge, CardBody, CardHeader
} from 'reactstrap';
import { Play, CheckCircle, AlertTriangle, XCircle, Server, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API from '../services/api';

const EppTools = () => {
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [domains, setDomains] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const eventSourceRef = useRef(null);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    loadAccounts();

    // Cleanup event source on unmount or navigation
    const cleanup = () => {
      if (eventSourceRef.current) {
        try {
          // ReadableStreamDefaultReader has cancel() method
          if (typeof eventSourceRef.current.cancel === 'function') {
            eventSourceRef.current.cancel();
          }
        } catch (e) {
          console.warn('Error cleaning up stream:', e);
        }
        eventSourceRef.current = null;
      }
      setIsChecking(false);
    };

    // Add beforeunload handler to cleanup streams on navigation
    const handleBeforeUnload = () => {
      cleanup();
    };

    // Add visibility change handler to refresh accounts when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && !isChecking) {
        loadAccounts();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cleanup();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isChecking]);

  // Handle location changes (navigation away from EPP Tools)
  useEffect(() => {
    return () => {
      // This cleanup runs when the component unmounts (navigation)
      if (eventSourceRef.current) {
        try {
          // ReadableStreamDefaultReader has cancel() method
          if (typeof eventSourceRef.current.cancel === 'function') {
            eventSourceRef.current.cancel();
          }
        } catch (e) {
          console.warn('Error cleaning up stream on navigation:', e);
        }
        eventSourceRef.current = null;
      }
      setIsChecking(false);
    };
  }, [location.pathname]);

  const loadAccounts = async () => {
    try {
      const response = await API.get('/epp/accounts');
      setAccounts(response.data.accounts || []);
    } catch (err) {
      setError('Failed to load EPP accounts: ' + (err.response?.data?.error || err.message));
    }
  };

  const showAlert = (type, message) => {
    if (type === 'error') {
      setError(message);
      setSuccess('');
    } else {
      setSuccess(message);
      setError('');
    }
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAccount) {
      showAlert('error', 'Please select an EPP account');
      return;
    }

    if (!domains.trim()) {
      showAlert('error', 'Please enter domains to check');
      return;
    }

    // Parse domains from textarea
    const domainList = domains
      .split(/[\n,;]/)
      .map(d => d.trim().toLowerCase())
      .filter(d => d.length > 0);

    if (domainList.length === 0) {
      showAlert('error', 'No valid domains found');
      return;
    }

    if (domainList.length > 500) {
      showAlert('error', 'Maximum 500 domains allowed');
      return;
    }

    // Check for duplicates
    const uniqueDomains = [...new Set(domainList)];
    if (uniqueDomains.length !== domainList.length) {
      showAlert('error', `${domainList.length - uniqueDomains.length} duplicate domains found. Please remove duplicates.`);
      return;
    }

    setIsChecking(true);
    setResults([]);
    setProgress({ completed: 0, total: uniqueDomains.length });
    setError('');
    setSuccess('');

    try {
      // Start the bulk check with Server-Sent Events
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/epp/tools/domain-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_id: parseInt(selectedAccount),
          domains: uniqueDomains
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Request failed');
      }

      // Handle Server-Sent Events
      const reader = response.body.getReader();
      eventSourceRef.current = reader; // Store reader for stop functionality
      const decoder = new TextDecoder();
      let buffer = '';

      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              setIsChecking(false);
              break;
            }

            // Check if component is still mounted
            if (!eventSourceRef.current) return;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop(); // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.substring(6));

                  if (data.type === 'started') {
                    setProgress({ completed: 0, total: data.total });
                  } else if (data.type === 'result') {
                    setResults(prev => [...prev, data]);
                    setProgress(prev => ({ ...prev, completed: data.index }));
                  } else if (data.type === 'completed') {
                    setIsChecking(false);
                    showAlert('success', 'Domain check completed successfully');
                    return;
                  }
                } catch (e) {
                  console.error('Error parsing SSE data:', e);
                }
              }
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Stream reading error:', error);
            setIsChecking(false);
            showAlert('error', 'Connection to server lost');
          }
        } finally {
          // Clean up reader reference
          if (eventSourceRef.current === reader) {
            eventSourceRef.current = null;
          }
        }
      };

      readStream();

    } catch (err) {
      setIsChecking(false);
      showAlert('error', 'Failed to start domain check: ' + err.message);
    }
  };

  const handleStop = () => {
    if (eventSourceRef.current) {
      try {
        if (typeof eventSourceRef.current.cancel === 'function') {
          eventSourceRef.current.cancel();
        }
      } catch (e) {
        // Reader might already be closed
        console.warn('Error stopping stream:', e);
      }
    }
    eventSourceRef.current = null;
    setIsChecking(false);
    showAlert('success', 'Domain check stopped');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircle size={16} className="text-success" />;
      case 'taken':
        return <AlertTriangle size={16} className="text-warning" />;
      case 'error':
        return <XCircle size={16} className="text-danger" />;
      default:
        return <Spinner size="sm" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge className="bg-dark text-success border border-success">Available</Badge>;
      case 'taken':
        return <Badge className="bg-dark text-warning border border-warning">Taken</Badge>;
      case 'error':
        return <Badge className="bg-dark text-danger border border-danger">Error</Badge>;
      default:
        return <Badge className="bg-dark text-secondary border border-secondary">Processing</Badge>;
    }
  };

  const selectedAccountInfo = accounts.find(acc => acc.id === parseInt(selectedAccount));

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <Globe className="page-heading-icon" />
            EPP Tools
          </h2>
          <small className="text-muted">Bulk domain availability checking and EPP utilities</small>
        </Col>
      </Row>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
          {error}
          <button type="button" className="btn-close-enhanced" onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
          {success}
          <button type="button" className="btn-close-enhanced" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <Row>
        <Col lg={4}>
          <Card className="bg-dark border-secondary">
            <CardHeader className="bg-secondary border-bottom border-secondary">
              <h5 className="mb-0">
                <Server size={20} className="me-2" />
                Bulk Domain Check
              </h5>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label for="account">EPP Account</Label>
                  <Input
                    type="select"
                    id="account"
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    disabled={isChecking}
                  >
                    <option value="">Select EPP Account</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({account.registrar_category.name})
                        {account.is_connected && ' ⚡'}
                      </option>
                    ))}
                  </Input>
                  {selectedAccountInfo && (
                    <div className="mt-2 text-muted small">
                      <div><strong>Registry:</strong> {selectedAccountInfo.registrar_category.name}</div>
                      <div><strong>Connection:</strong>
                        {selectedAccountInfo.is_connected ? (
                          <span className="text-warning ms-1">⚡ Persistent</span>
                        ) : (
                          <span className="text-secondary ms-1">Temporary</span>
                        )}
                      </div>
                      {selectedAccountInfo.batch_settings && (
                        <div><strong>Batch Check:</strong>
                          {selectedAccountInfo.batch_settings.domain_check_multicheck_enabled ? (
                            <span className="text-info ms-1">
                              ⚡ {selectedAccountInfo.batch_settings.domain_check_batch_size} domains per check
                            </span>
                          ) : (
                            <span className="text-secondary ms-1">Single domain checks</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label for="domains">
                    Domains to Check
                    <small className="text-muted">(max 500, one per line or comma-separated)</small>
                  </Label>
                  <Input
                    type="textarea"
                    id="domains"
                    rows="8"
                    value={domains}
                    onChange={(e) => setDomains(e.target.value)}
                    disabled={isChecking}
                    placeholder="example.com&#10;test.org&#10;sample.net"
                  />
                  <div className="mt-1 text-muted small">
                    Domains count: {domains.split(/[\n,;]/).filter(d => d.trim().length > 0).length}
                  </div>
                </FormGroup>

                {isChecking ? (
                  <Button color="danger" size="sm" onClick={handleStop} className="w-100">
                    <XCircle size={16} className="me-1" />
                    Stop Check
                  </Button>
                ) : (
                  <Button color="primary" size="sm" type="submit" className="w-100">
                    <Play size={16} className="me-1" />
                    Start Check
                  </Button>
                )}
              </Form>

              {isChecking && progress.total > 0 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between small text-muted mb-1">
                    <span>Progress</span>
                    <span>{progress.completed}/{progress.total}</span>
                  </div>
                  <Progress
                    value={(progress.completed / progress.total) * 100}
                    color="info"
                    striped
                    animated
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="bg-dark border-secondary">
            <CardHeader className="bg-secondary border-bottom border-secondary">
              <h5 className="mb-0">Results</h5>
            </CardHeader>
            <CardBody>
              {results.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <Globe size={48} className="mb-3 opacity-50" />
                  <p>No results yet. Start a domain check to see results here.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table dark striped size="sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Domain</th>
                        <th>Status</th>
                        <th>Message</th>
                        <th>Time (ms)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((result, index) => (
                        <tr key={index}>
                          <td>{result.index}</td>
                          <td className="font-monospace">{result.domain}</td>
                          <td>
                            {getStatusIcon(result.status)}
                            <span className="ms-2">{getStatusBadge(result.status)}</span>
                          </td>
                          <td>
                            <span className={
                              result.status === 'available' ? 'text-success' :
                              result.status === 'taken' ? 'text-warning' :
                              'text-danger'
                            }>
                              {result.message}
                            </span>
                          </td>
                          <td className="text-muted">{result.duration_ms}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EppTools;